import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, FileText, AlertTriangle, CheckCircle, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DORAExecutiveSummary } from './DORAExecutiveSummary';
import api from '@/services/api';

interface Change {
  paragraph_index: number;
  blank_id: number;
  original: string;
  modified: string;
  question: string;
  confidence: number;
  source_doc_id?: number;
}

interface Phase2Difference {
  topic: string;
  severity: 'CRITICAL' | 'MEDIUM' | 'LOW';
  buyer_text: string;
  seller_text: string;
  differences: string[];
  commercial_impact: string;
  recommendation: string;
  section_reference: string;
  buyer_paragraph_index?: number;  // START of section in ORIGINAL filled doc (LEFT side)
  buyer_paragraph_end?: number;  // END of section in ORIGINAL filled doc (for multi-paragraph highlighting)
  paragraph_index?: number;  // Location in GENERATED doc after insertions (RIGHT side)
}

interface DORADiffViewProps {
  projectId: number;
  templateDocId: number;
  generatedDocId: number;
  changes: Change[];
  summary: {
    total_changes: number;
    high_confidence: number;
    needs_review: number;
  };
  modelName?: string;
  generationTimeSeconds?: number;
  phase2Data?: {
    status: string;
    differences: Phase2Difference[];
    summary: {
      total_issues: number;
      critical_issues: number;
      medium_issues: number;
      low_issues: number;
    };
  };
  phase2Error?: string;
}

// Unified item type for navigation
interface UnifiedItem {
  type: 'phase1' | 'phase2';
  index: number;
  buyer_paragraph_index?: number;  // For Phase 2: LEFT side start (original doc)
  buyer_paragraph_end?: number;  // For Phase 2: LEFT side end (for multi-paragraph sections)
  paragraph_index?: number;  // For Phase 1: both sides; For Phase 2: RIGHT side (generated doc)
  severity?: 'CRITICAL' | 'MEDIUM' | 'LOW';
  phase1Data?: Change;
  phase2Data?: Phase2Difference;
}

export default function DORADiffView({
  projectId,
  templateDocId,
  generatedDocId,
  changes,
  summary,
  modelName,
  generationTimeSeconds,
  phase2Data,
  phase2Error
}: DORADiffViewProps) {
  console.log('🚀 DORADiffView COMPONENT RENDERING', { 
    templateDocId, 
    generatedDocId,
    changesCount: changes.length,
    phase2Count: phase2Data?.differences?.length || 0
  });
  
  const [selectedChangeIndex, setSelectedChangeIndex] = useState(0);
  const [originalContent, setOriginalContent] = useState<string | string[]>('');
  const [generatedContent, setGeneratedContent] = useState<string | string[]>('');
  const [loading, setLoading] = useState(true);
  const [editedChanges, setEditedChanges] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [isExcelFile, setIsExcelFile] = useState(false);
  const [generatedFilename, setGeneratedFilename] = useState<string>('generated.docx');
  
  // Build unified navigation list
  const unifiedItems: UnifiedItem[] = [
    ...changes.map((change, idx) => ({
      type: 'phase1' as const,
      index: idx,
      phase1Data: change,
      paragraph_index: change.paragraph_index
    })),
    ...(phase2Data?.differences || []).map((diff, idx) => ({
      type: 'phase2' as const,
      index: idx,
      severity: diff.severity,
      phase2Data: diff,
      buyer_paragraph_index: diff.buyer_paragraph_index,  // LEFT side start (original doc)
      buyer_paragraph_end: diff.buyer_paragraph_end,  // LEFT side end (for multi-paragraph sections)
      paragraph_index: diff.paragraph_index  // RIGHT side (generated doc)
    }))
  ].sort((a, b) => {
    // Sort by buyer_paragraph_index for Phase 2, paragraph_index for Phase 1
    const aIdx = (a.type === 'phase2' ? a.buyer_paragraph_index : a.paragraph_index) ?? 999999;
    const bIdx = (b.type === 'phase2' ? b.buyer_paragraph_index : b.paragraph_index) ?? 999999;
    return aIdx - bIdx;
  });

  // DEBUG: Log unified items on component render - ALWAYS RUN
  useEffect(() => {
    if (unifiedItems.length === 0) {
      console.warn('⚠️ WARNING: No unified items to display!');
      return;
    }
    
    console.log('================================================================================');
    console.log('🔍 UNIFIED ITEMS DEBUG - FULL LIST:');
    console.log('Total items:', unifiedItems.length);
    console.log('Phase 1 (green) count:', unifiedItems.filter(i => i.type === 'phase1').length);
    console.log('Phase 2 (red/orange) count:', unifiedItems.filter(i => i.type === 'phase2').length);
    console.log('\n📋 ITEM BY ITEM BREAKDOWN:');
    unifiedItems.forEach((item, idx) => {
      if (item.type === 'phase2') {
        console.log(`  [${idx}] ${item.type.toUpperCase()} | ` +
          `Severity: ${item.severity} | ` +
          `Topic: ${item.phase2Data?.topic} | ` +
          `LEFT(buyer): para ${item.buyer_paragraph_index} | ` +
          `RIGHT(generated): para ${item.paragraph_index}`);
      } else {
        console.log(`  [${idx}] ${item.type.toUpperCase()} | ` +
          `Para: ${item.paragraph_index}`);
      }
    });
    console.log('================================================================================');
  }, [unifiedItems.length]);

  // DEBUG: Log detailed info when navigating to a different item
  useEffect(() => {
    const currentItem = unifiedItems[selectedChangeIndex];
    if (!currentItem) {
      console.error('❌ NO ITEM AT INDEX:', selectedChangeIndex);
      return;
    }

    console.group(`📍 NAVIGATION DEBUG - Item ${selectedChangeIndex + 1}/${unifiedItems.length}`);
    console.log('Current selectedChangeIndex:', selectedChangeIndex);
    console.log('Item type:', currentItem.type);
    console.log('Item paragraph_index:', currentItem.paragraph_index);
    
    if (currentItem.type === 'phase1' && currentItem.phase1Data) {
      const data = currentItem.phase1Data;
      console.log('📝 TYPE: Template Blank Fill');
      console.log('  Question/Placeholder:', data.question);
      console.log('  Location: Paragraph', data.paragraph_index);
      console.log('  Confidence:', `${(data.confidence * 100).toFixed(0)}%`);
    } else if (currentItem.type === 'phase2' && currentItem.phase2Data) {
      const data = currentItem.phase2Data;
      console.log('⚠️ TYPE: Compliance Issue');
      console.log('  Severity:', data.severity);
      console.log('  Topic:', data.topic.replace(/_/g, ' ').toUpperCase());
      console.log('  Section:', data.section_reference);
      console.log('  Buyer text preview:', data.buyer_text.substring(0, 100) + '...');
      
      // Check if this item will be matched to any paragraph
      console.log('\n🔍 MATCHING CHECK:');
      console.log('  Will this item appear in document paragraphs or unmatched section?');
      console.log('  (Check if bg-red/orange/blue-200 appears after navigation)');
    }
    
    console.groupEnd();
    
    // After a short delay, verify the item is actually visible
    setTimeout(() => {
      const selectedElements = document.querySelectorAll('.bg-red-200, .bg-orange-200, .bg-blue-200, .bg-green-200');
      console.log(`✅ Found ${selectedElements.length} selected (highlighted) elements in DOM`);
      if (selectedElements.length === 0 && currentItem.type === 'phase2') {
        console.warn('⚠️ WARNING: Phase II item is selected but NO highlighted element found in DOM!');
        console.warn('   This means the item is not being rendered or matched properly.');
      }
    }, 200);
  }, [selectedChangeIndex, unifiedItems]);

  const originalRef = useRef<HTMLDivElement>(null);
  const generatedRef = useRef<HTMLDivElement>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  // Load document contents
  useEffect(() => {
    loadDocuments();
  }, [templateDocId, generatedDocId]);

  const loadDocuments = async () => {
    // Validate document IDs
    if (!templateDocId || !generatedDocId) {
      console.error('Invalid document IDs:', { templateDocId, generatedDocId });
      console.error('Invalid document IDs - cannot load documents');
      return;
    }

    try {
      setLoading(true);
      
      console.log('Loading documents:', { templateDocId, generatedDocId });
      
      // Fetch both documents with authentication
      const [originalRes, generatedRes] = await Promise.all([
        api.get(`/documents/${templateDocId}/content`),
        api.get(`/documents/${generatedDocId}/content`)
      ]);

      // Check if this is an Excel file
      const isExcel = originalRes.data.filename?.endsWith('.xlsx') || generatedRes.data.filename?.endsWith('.xlsx');
      setIsExcelFile(isExcel);
      setGeneratedFilename(generatedRes.data.filename || 'generated.docx');

      console.log('📄 DORA Documents loaded:', {
        templateDocId,
        generatedDocId,
        isExcel,
        templateFilename: originalRes.data.filename,
        generatedFilename: generatedRes.data.filename,
        originalLength: originalRes.data.content?.length || 0,
        generatedLength: generatedRes.data.content?.length || 0,
        originalParagraphs: originalRes.data.paragraphs?.length || 0,
        generatedParagraphs: generatedRes.data.paragraphs?.length || 0,
        contentSame: originalRes.data.content === generatedRes.data.content
      });

      // Use paragraphs array if available (preserves exact indices)
      // Otherwise fall back to content string
      if (originalRes.data.paragraphs && generatedRes.data.paragraphs) {
        setOriginalContent(originalRes.data.paragraphs);
        setGeneratedContent(generatedRes.data.paragraphs);
      } else {
        setOriginalContent(originalRes.data.content || '');
        setGeneratedContent(generatedRes.data.content || '');
      }
    } catch (error: any) {
      console.error('Failed to load documents:', error);
      console.error('Error details:', {
        templateDocId,
        generatedDocId,
        error: error.response?.data || error.message
      });
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Synchronized scrolling
  const handleScroll = (source: 'original' | 'generated') => (e: React.UIEvent<HTMLDivElement>) => {
    if (isSyncing) return;
    
    setIsSyncing(true);
    const sourceEl = e.currentTarget;
    const targetEl = source === 'original' ? generatedRef.current : originalRef.current;
    
    if (targetEl) {
      const scrollRatio = sourceEl.scrollTop / (sourceEl.scrollHeight - sourceEl.clientHeight);
      targetEl.scrollTop = scrollRatio * (targetEl.scrollHeight - targetEl.clientHeight);
    }
    
    setTimeout(() => setIsSyncing(false), 50);
  };

  // Navigate to specific change
  const jumpToChange = (index: number) => {
    const targetItem = unifiedItems[index];
    console.log('');
    console.log('🔄🔄🔄 JUMP TO CHANGE CALLED 🔄🔄🔄');
    console.log(`Index ${index}/${unifiedItems.length - 1}`, {
      type: targetItem?.type,
      severity: targetItem?.severity,
      topic: targetItem?.type === 'phase2' ? targetItem?.phase2Data?.topic : 'N/A',
      paragraph_index: targetItem?.paragraph_index
    });
    
    setSelectedChangeIndex(index);
    
    // Scroll to the highlighted section in both documents
    setTimeout(() => {
      console.log('⏱️ Starting scroll timeout (100ms delay passed)...');
      
      // Find highlighted elements in both documents
      const originalScrollArea = originalRef.current;
      const generatedScrollArea = generatedRef.current;
      
      console.log('📜 Scroll areas:', {
        originalExists: !!originalScrollArea,
        generatedExists: !!generatedScrollArea
      });
      
      if (originalScrollArea && generatedScrollArea) {
        // Look for selected items with darker backgrounds (Phase I: bg-green-200, Phase II: bg-red/orange/blue-200)
        const selectedSelectors = [
          '.bg-green-200',   // Phase I selected
          '.bg-red-200',     // Phase II CRITICAL selected
          '.bg-orange-200',  // Phase II MEDIUM selected
          '.bg-blue-200'     // Phase II LOW selected
        ];
        
        console.log('🔍 Searching for highlighted elements with selectors:', selectedSelectors);
        
        let originalHighlight = null;
        let generatedHighlight = null;
        
        // Find ALL matching elements, then prefer the one with data-scroll-target="buyer-text"
        // This ensures we skip recommendation paragraphs and target the actual buyer text
        for (const selector of selectedSelectors) {
          if (!originalHighlight) {
            const allMatches = originalScrollArea.querySelectorAll(selector);
            // Prefer elements with data-scroll-target="buyer-text"
            for (const el of Array.from(allMatches)) {
              if (el.getAttribute('data-scroll-target') === 'buyer-text') {
                originalHighlight = el;
                console.log(`✅ LEFT: Found buyer text with ${selector}`);
                break;
              }
            }
            // Fallback to first match if no buyer-text found
            if (!originalHighlight && allMatches.length > 0) {
              originalHighlight = allMatches[0];
              console.log(`✅ LEFT: Found highlight with ${selector} (fallback)`);
            }
          }
          if (!generatedHighlight) {
            const allMatches = generatedScrollArea.querySelectorAll(selector);
            // Prefer elements with data-scroll-target="buyer-text" (skips recommendation paragraphs)
            for (const el of Array.from(allMatches)) {
              if (el.getAttribute('data-scroll-target') === 'buyer-text') {
                generatedHighlight = el;
                console.log(`✅ RIGHT: Found buyer text with ${selector} (skipping recommendation)`);
                break;
              }
            }
            // Fallback to first match if no buyer-text found
            if (!generatedHighlight && allMatches.length > 0) {
              generatedHighlight = allMatches[0];
              console.log(`✅ RIGHT: Found highlight with ${selector} (fallback)`);
            }
          }
          if (originalHighlight && generatedHighlight) break;
        }
        
        console.log('📍 Scroll targets found:', {
          leftFound: !!originalHighlight,
          rightFound: !!generatedHighlight,
          leftText: originalHighlight ? (originalHighlight as HTMLElement).innerText.substring(0, 50) + '...' : 'N/A',
          rightText: generatedHighlight ? (generatedHighlight as HTMLElement).innerText.substring(0, 50) + '...' : 'N/A'
        });
        
        if (originalHighlight) {
          console.log('⬅️ Scrolling LEFT document to highlight...');
          originalHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          console.warn('⚠️ No highlight found on LEFT document!');
        }
        
        if (generatedHighlight) {
          console.log('➡️ Scrolling RIGHT document to highlight...');
          generatedHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          console.warn('⚠️ No highlight found on RIGHT document!');
        }
      } else {
        console.error('❌ Scroll areas not found in DOM!');
      }
      
      console.log('✅ Jump to change complete\n');
    }, 100); // Small delay to ensure DOM updates
  };

  // Download documents
  const downloadDocument = async (docId: number, filename: string) => {
    try {
      const response = await api.get(`/documents/${docId}/download`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log('Downloaded:', filename);
    } catch (error) {
      console.error('Download failed:', error);
      console.error('Failed to download document');
    }
  };

  // Render document content with highlights
  const renderContentWithHighlights = (content: string | string[], isOriginal: boolean) => {
    // Get paragraphs - either from array or by splitting string
    const paragraphs = Array.isArray(content) ? content : content.split('\n\n');
    
    // Track which phase2 items have been matched to paragraphs
    const matchedPhase2Items = new Set<number>();
    
    // Build O(1) lookup map: paragraph_index -> Phase II items (PERFORMANCE FIX)
    const phase2ByParagraph = new Map<number, UnifiedItem[]>();
    unifiedItems.forEach(item => {
      if (item.type === 'phase2' && item.paragraph_index !== undefined) {
        const existing = phase2ByParagraph.get(item.paragraph_index) || [];
        existing.push(item);
        phase2ByParagraph.set(item.paragraph_index, existing);
      }
    });
    
    const paragraphElements = paragraphs.map((para, idx) => {
      // Check if this paragraph has a Phase I change
      const phase1Change = changes.find(c => c.paragraph_index === idx);
      const phase1Item = unifiedItems.find(item => 
        item.type === 'phase1' && item.paragraph_index === idx
      );
      
      // Phase II matching: Direct O(1) index lookup on BOTH sides
      // - LEFT (original): Check if paragraph is within section range (buyer_paragraph_index to buyer_paragraph_end)
      // - RIGHT (generated): Use paragraph_index (where recommendation was inserted)
      let phase2Item: UnifiedItem | undefined;
      if (isOriginal) {
        // LEFT: Check if paragraph is within the section range for multi-paragraph highlighting
        phase2Item = unifiedItems.find(item => {
          if (item.type !== 'phase2') return false;
          const start = item.buyer_paragraph_index;
          if (start === undefined) return false;
          const end = item.buyer_paragraph_end ?? start;  // If no end, use start (single paragraph)
          return idx >= start && idx <= end;
        });
      } else {
        // RIGHT: Direct O(1) lookup using paragraph_index
        const phase2Items = phase2ByParagraph.get(idx) || [];
        phase2Item = phase2Items.length > 0 ? phase2Items[0] : undefined;
      }
      
      // On RIGHT side, also check if this paragraph IS the recommendation text itself
      let phase2RecommendationMatch: UnifiedItem | undefined;
      if (!isOriginal && !phase2Item) {
        phase2RecommendationMatch = unifiedItems.find(item => 
          item.type === 'phase2' && 
          item.phase2Data?.recommendation && 
          para.trim().length > 50 &&
          para.includes(item.phase2Data.recommendation.substring(0, 100))
        );
      }
      
      const matchedItem = phase1Item || phase2Item || phase2RecommendationMatch;
      const isSelected = matchedItem && unifiedItems.indexOf(matchedItem) === selectedChangeIndex;
      
      // Handle recommendation paragraphs on RIGHT side (inserted by backend)
      if (phase2RecommendationMatch && !isOriginal) {
        // Track that this phase2 item was matched
        const phase2Index = unifiedItems.indexOf(phase2RecommendationMatch);
        matchedPhase2Items.add(phase2Index);
        
        const colors = getItemColor(phase2RecommendationMatch);
        const bgColor = isSelected ? 
          (phase2RecommendationMatch.severity === 'CRITICAL' ? 'bg-red-200' : 
           phase2RecommendationMatch.severity === 'MEDIUM' ? 'bg-orange-200' : 'bg-blue-200') :
          (phase2RecommendationMatch.severity === 'CRITICAL' ? 'bg-red-50' : 
           phase2RecommendationMatch.severity === 'MEDIUM' ? 'bg-orange-50' : 'bg-blue-50');
        const borderColor = isSelected ? 
          (phase2RecommendationMatch.severity === 'CRITICAL' ? 'border-red-600' : 
           phase2RecommendationMatch.severity === 'MEDIUM' ? 'border-orange-600' : 'border-blue-600') :
          colors.border;
        
        return (
          <div
            key={idx}
            className={`p-3 mb-2 border-l-4 ${bgColor} ${borderColor} rounded cursor-pointer hover:opacity-80 transition-all max-w-full overflow-hidden`}
            onClick={() => jumpToChange(unifiedItems.indexOf(phase2RecommendationMatch))}
          >
            <p className="text-sm whitespace-pre-wrap break-words overflow-wrap-anywhere max-w-full">{para}</p>
          </div>
        );
      }
      
      if (phase1Change && phase1Item) {
        // Phase I change - GREEN
        const text = isOriginal ? phase1Change.original : phase1Change.modified;
        const bgColor = isSelected ? 'bg-green-200' : 'bg-green-50';
        const borderColor = isSelected ? 'border-green-600' : 'border-green-400';
        
        // LEFT SIDE (original): Show ONLY text with green highlight - no badges
        if (isOriginal) {
          return (
            <div
              key={idx}
              className={`p-3 mb-2 border-l-4 ${bgColor} ${borderColor} rounded cursor-pointer hover:bg-green-100 transition-colors max-w-full overflow-hidden`}
              onClick={() => jumpToChange(unifiedItems.indexOf(phase1Item))}
            >
              <p className="text-sm whitespace-pre-wrap break-words overflow-wrap-anywhere">{text}</p>
            </div>
          );
        }
        
        // RIGHT SIDE (generated): Show filled answer only (clean, no badges)
        return (
          <div
            key={idx}
            className={`p-3 mb-2 border-l-4 ${bgColor} ${borderColor} rounded cursor-pointer hover:bg-green-100 transition-colors max-w-full overflow-hidden`}
            onClick={() => jumpToChange(unifiedItems.indexOf(phase1Item))}
          >
            <p className="text-sm whitespace-pre-wrap break-words overflow-wrap-anywhere">{text}</p>
          </div>
        );
      }
      
      if (phase2Item && phase2Item.phase2Data) {
        // Track that this phase2 item was matched
        const phase2Index = unifiedItems.indexOf(phase2Item);
        matchedPhase2Items.add(phase2Index);
        
        const colors = getItemColor(phase2Item);
        const bgColor = isSelected ? 
          (phase2Item.severity === 'CRITICAL' ? 'bg-red-200' : 
           phase2Item.severity === 'MEDIUM' ? 'bg-orange-200' : 'bg-blue-200') :
          (phase2Item.severity === 'CRITICAL' ? 'bg-red-50' : 
           phase2Item.severity === 'MEDIUM' ? 'bg-orange-50' : 'bg-blue-50');
        const borderColor = isSelected ? 
          (phase2Item.severity === 'CRITICAL' ? 'border-red-600' : 
           phase2Item.severity === 'MEDIUM' ? 'border-orange-600' : 'border-blue-600') :
          colors.border;
        
        // LEFT SIDE (original): Show ONLY original document text with color highlight - no badges/labels
        if (isOriginal) {
          return (
            <div
              key={idx}
              data-scroll-target="buyer-text"
              className={`p-3 mb-2 border-l-4 ${bgColor} ${borderColor} rounded cursor-pointer hover:opacity-80 transition-all max-w-full overflow-hidden`}
              onClick={() => jumpToChange(unifiedItems.indexOf(phase2Item))}
            >
              <p className="text-sm whitespace-pre-wrap break-words overflow-wrap-anywhere max-w-full">{para}</p>
            </div>
          );
        }
        
        // RIGHT SIDE (generated): Render buyer text with highlighting for scrolling target
        // Mark this as primary scroll target to skip recommendation paragraphs
        return (
          <div
            key={idx}
            data-scroll-target="buyer-text"
            className={`p-3 mb-2 border-l-4 ${bgColor} ${borderColor} rounded cursor-pointer hover:opacity-80 transition-all max-w-full overflow-hidden`}
            onClick={() => jumpToChange(unifiedItems.indexOf(phase2Item))}
          >
            <p className="text-sm whitespace-pre-wrap break-words overflow-wrap-anywhere max-w-full">{para}</p>
          </div>
        );
      }
      
      return (
        <div key={idx} className="max-w-full overflow-hidden">
          <p className="text-sm mb-4 text-gray-700 whitespace-pre-wrap break-words overflow-wrap-anywhere">
            {para}
          </p>
        </div>
      );
    });
    
    // Add any unmatched Phase II items at the end (so ALL compliance issues are visible)
    const unmatchedPhase2Elements = unifiedItems
      .map((item, idx) => ({ item, idx }))
      .filter(({ item, idx }) => item.type === 'phase2' && !matchedPhase2Items.has(idx))
      .map(({ item, idx }) => {
        const phase2Data = item.phase2Data!;
        const colors = getItemColor(item);
        const isSelected = idx === selectedChangeIndex;
        const bgColor = isSelected ? 
          (item.severity === 'CRITICAL' ? 'bg-red-200' : 
           item.severity === 'MEDIUM' ? 'bg-orange-200' : 'bg-blue-200') :
          (item.severity === 'CRITICAL' ? 'bg-red-50' : 
           item.severity === 'MEDIUM' ? 'bg-orange-50' : 'bg-blue-50');
        const borderColor = isSelected ? 
          (item.severity === 'CRITICAL' ? 'border-red-600' : 
           item.severity === 'MEDIUM' ? 'border-orange-600' : 'border-blue-600') :
          colors.border;
        
        // LEFT SIDE (original): Show ONLY buyer text with highlight - NO seller/recommendation
        if (isOriginal) {
          return (
            <div
              key={`unmatched-phase2-${idx}`}
              className={`p-3 mb-2 border-l-4 ${bgColor} ${borderColor} rounded cursor-pointer hover:opacity-80 transition-all max-w-full overflow-hidden`}
              onClick={() => jumpToChange(idx)}
            >
              <p className="text-sm whitespace-pre-wrap break-words overflow-wrap-anywhere max-w-full">{phase2Data.buyer_text}</p>
            </div>
          );
        }
        
        // RIGHT SIDE (generated): Show full details for unmatched items
        return (
          <div
            key={`unmatched-phase2-${idx}`}
            className={`p-3 mb-2 border-l-4 ${bgColor} ${borderColor} rounded cursor-pointer hover:opacity-80 transition-all max-w-full overflow-hidden`}
            onClick={() => jumpToChange(idx)}
          >
            <div className="flex items-center gap-2 mb-2">
              <Badge className={colors.badge + ' ' + colors.text}>Compliance Issue</Badge>
              <Badge className={colors.badge + ' ' + colors.text}>
                {phase2Data.severity}
              </Badge>
            </div>
            <p className="text-sm font-semibold mb-1 break-words word-wrap">
              {phase2Data.topic.replace(/_/g, ' ').toUpperCase()}
            </p>
            <div className="text-sm space-y-2 max-w-full">
              <div>
                <p className="font-medium break-words">Buyer Requirement:</p>
                <p className="text-muted-foreground whitespace-pre-wrap break-words word-wrap overflow-wrap-anywhere">{phase2Data.buyer_text}</p>
              </div>
              <div>
                <p className="font-medium break-words">Seller Position:</p>
                <p className="text-muted-foreground whitespace-pre-wrap break-words word-wrap overflow-wrap-anywhere">{phase2Data.seller_text}</p>
              </div>
              <div>
                <p className="font-medium break-words">Recommendation:</p>
                <p className="text-muted-foreground whitespace-pre-wrap break-words word-wrap overflow-wrap-anywhere">{phase2Data.recommendation}</p>
              </div>
            </div>
          </div>
        );
      });
    
    // Return all elements: matched paragraphs + unmatched phase2 items
    return [...paragraphElements, ...unmatchedPhase2Elements];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const handleEditChange = (changeIndex: number, newValue: string) => {
    setEditedChanges(prev => ({
      ...prev,
      [changeIndex]: newValue
    }));
  };

  const handleSaveChange = async (changeIndex: number) => {
    const change = changes[changeIndex];
    if (!change) return;
    
    const newValue = editedChanges[changeIndex] || change.modified;
    
    try {
      setSaving(true);
      await api.put(`/dora/projects/${projectId}/blanks/${change.blank_id}`, {
        answer_value: newValue
      });
      
      // Update the local change
      change.modified = newValue;
      
      // Clear edited state
      setEditedChanges(prev => {
        const updated = { ...prev };
        delete updated[changeIndex];
        return updated;
      });
      
      // Regenerate document with updated changes
      await api.post(`/dora/projects/${projectId}/generate`);
      
      // Reload documents to show updated content
      await loadDocuments();
    } catch (error: any) {
      console.error('Failed to save change:', error);
    } finally {
      setSaving(false);
    }
  };

  // Helper to get color based on item type and severity
  const getItemColor = (item: UnifiedItem) => {
    if (item.type === 'phase1') {
      return {
        border: 'border-green-400',
        bg: 'bg-green-50',
        text: 'text-green-800',
        badge: 'bg-green-100'
      };
    } else {
      switch (item.severity) {
        case 'CRITICAL':
          return {
            border: 'border-red-400',
            bg: 'bg-red-50',
            text: 'text-red-800',
            badge: 'bg-red-100'
          };
        case 'MEDIUM':
          return {
            border: 'border-orange-400',
            bg: 'bg-orange-50',
            text: 'text-orange-800',
            badge: 'bg-orange-100'
          };
        case 'LOW':
          return {
            border: 'border-blue-400',
            bg: 'bg-blue-50',
            text: 'text-blue-800',
            badge: 'bg-blue-100'
          };
        default:
          return {
            border: 'border-gray-400',
            bg: 'bg-gray-50',
            text: 'text-gray-800',
            badge: 'bg-gray-100'
          };
      }
    }
  };

  // Get current item from unified list
  const currentItem = unifiedItems[selectedChangeIndex];
  const currentChange = currentItem?.phase1Data;
  const currentPhase2 = currentItem?.phase2Data;
  const currentEditedValue = currentChange && editedChanges[selectedChangeIndex] !== undefined 
    ? editedChanges[selectedChangeIndex] 
    : currentChange?.modified;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold">DORA Compliance Analysis</h2>
        {(modelName || (generationTimeSeconds !== undefined && generationTimeSeconds !== null)) && (
          <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
            {modelName && (
              <span>Model: <span className="font-medium">{modelName}</span></span>
            )}
            {generationTimeSeconds !== undefined && generationTimeSeconds !== null && (
              <span>Analysis Time: <span className="font-medium">
                {Math.floor(generationTimeSeconds / 60)}m {Math.floor(generationTimeSeconds % 60)}s
              </span></span>
            )}
          </div>
        )}
      </div>

      {/* Phase II Executive Summary */}
      {phase2Data && (
        <DORAExecutiveSummary phase2Data={phase2Data} />
      )}

      {/* Phase II Error Display */}
      {phase2Error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Compliance Analysis Warning:</strong> {phase2Error}
          </AlertDescription>
        </Alert>
      )}

      {/* Change details and editor - STICKY AT TOP */}
      {currentItem && (
        <Card className={`sticky top-0 z-20 ${getItemColor(currentItem).border} ${getItemColor(currentItem).bg} shadow-lg`}>
          <CardContent className="pt-6">
            {/* Navigation bar */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selectedChangeIndex === 0}
                  onClick={() => {
                    console.log('======================================');
                    console.log('⬅️ PREVIOUS BUTTON CLICKED!');
                    console.log('Current index:', selectedChangeIndex);
                    console.log('Total items:', unifiedItems.length);
                    console.log('Current item:', unifiedItems[selectedChangeIndex]);
                    console.log('Previous item:', unifiedItems[selectedChangeIndex - 1]);
                    console.log('======================================');
                    jumpToChange(selectedChangeIndex - 1);
                  }}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm font-medium">
                  Item {selectedChangeIndex + 1} of {unifiedItems.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selectedChangeIndex === unifiedItems.length - 1}
                  onClick={() => {
                    console.warn('==================== NEXT BUTTON CLICKED ====================');
                    console.warn('🔴🔴🔴 NEXT BUTTON CLICKED! 🔴🔴🔴');
                    console.warn('Current index:', selectedChangeIndex);
                    console.warn('Total items:', unifiedItems.length);
                    console.warn('Current item:', unifiedItems[selectedChangeIndex]);
                    console.warn('Next item:', unifiedItems[selectedChangeIndex + 1]);
                    console.warn('==================== CALLING jumpToChange ====================');
                    jumpToChange(selectedChangeIndex + 1);
                  }}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {currentItem.type === 'phase1' && currentChange && (
                  <Badge variant={currentChange.confidence > 0.8 ? 'default' : 'secondary'}>
                    {(currentChange.confidence * 100).toFixed(0)}% confidence
                  </Badge>
                )}
                {currentItem.type === 'phase2' && currentPhase2 && (
                  <Badge className={getItemColor(currentItem).badge}>
                    {currentPhase2.severity}
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="space-y-4">
              
              {/* Editable sections - only show for non-Excel files */}
              {!isExcelFile && currentItem.type === 'phase1' && currentChange && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      Original:
                    </label>
                    <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded border">
                      <p className="text-sm font-mono">{currentChange.original}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      Modified: (editable)
                    </label>
                    <textarea
                      value={currentEditedValue}
                      onChange={(e) => handleEditChange(selectedChangeIndex, e.target.value)}
                      className="w-full p-3 bg-white dark:bg-gray-900 border border-green-300 rounded text-sm font-mono text-green-700 dark:text-green-400 min-h-[100px] focus:ring-2 focus:ring-green-500"
                      placeholder="Edit the modified text..."
                    />
                    {editedChanges[selectedChangeIndex] !== undefined && (
                      <Button
                        onClick={() => handleSaveChange(selectedChangeIndex)}
                        disabled={saving}
                        className="mt-2 bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        {saving ? 'Saving...' : 'Save & Regenerate'}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Phase II - Editable counter-proposal (streamlined - buyer/seller visible in docs) */}
              {!isExcelFile && currentItem.type === 'phase2' && currentPhase2 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge className={getItemColor(currentItem).badge + ' text-xs'}>
                        {currentPhase2.topic.replace(/_/g, ' ').toUpperCase()}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{currentPhase2.section_reference}</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      💡 Recommended Counter-Proposal (editable):
                    </label>
                    <textarea
                      defaultValue={currentPhase2.recommendation}
                      className={`w-full p-3 bg-white border ${getItemColor(currentItem).border} rounded text-sm min-h-[100px] focus:ring-2 focus:ring-offset-2`}
                      placeholder="Edit counter-proposal language..."
                    />
                    <div className="flex items-center justify-end mt-2">
                      <Button
                        className={`${getItemColor(currentItem).bg}`}
                        size="sm"
                      >
                        Save Counter-Proposal
                      </Button>
                    </div>
                  </div>
                  
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                      Show full comparison details
                    </summary>
                    <div className="mt-2 space-y-2 pl-4 border-l-2 border-gray-200">
                      <div>
                        <span className="font-medium">Current Buyer Language:</span>
                        <p className="text-muted-foreground mt-1">{currentPhase2.buyer_text}</p>
                      </div>
                      <div>
                        <span className="font-medium">Seller Standard:</span>
                        <p className="text-muted-foreground mt-1">{currentPhase2.seller_text}</p>
                      </div>
                      <div>
                        <span className="font-medium">Key Differences:</span>
                        <ul className="list-disc list-inside mt-1 text-muted-foreground">
                          {currentPhase2.differences.map((diff, i) => (
                            <li key={i}>{diff}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <span className="font-medium">Cost Impact:</span>
                        <p className="text-muted-foreground mt-1">
                          {currentPhase2.commercial_impact}
                          {currentPhase2.commercial_impact === '$$$$' && ' (Critical - major cost burden)'}
                          {currentPhase2.commercial_impact === '$$$' && ' (Medium-High - requires pricing adjustment)'}
                          {currentPhase2.commercial_impact === '$$' && ' (Low-Medium - noticeable but manageable)'}
                          {currentPhase2.commercial_impact === '$' && ' (Low - minor costs)'}
                        </p>
                      </div>
                    </div>
                  </details>
                </div>
              )}
              
              {(isExcelFile && currentChange) ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Total Changes</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold">{summary.total_changes}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-muted-foreground">High Confidence</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-green-600">{summary.high_confidence}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Needs Review</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-amber-600">{summary.needs_review}</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Excel Comparison View */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Excel Document Comparison
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Use the download buttons above to get the original template or generated file
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="border-t pt-6">
                        <h3 className="font-semibold mb-3">Changes Made ({changes.length})</h3>
                        <ScrollArea className="h-[400px]">
                          <div className="space-y-3">
                            {changes.map((change, idx) => (
                              <Card key={idx} className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <Badge variant={change.confidence > 0.8 ? 'default' : 'secondary'}>
                                    {change.confidence > 0.8 ? <CheckCircle className="h-3 w-3 mr-1" /> : <AlertCircle className="h-3 w-3 mr-1" />}
                                    {Math.round(change.confidence * 100)}% confidence
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground mb-2">{change.question}</p>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="font-medium mb-1">Original:</p>
                                    <p className="text-muted-foreground italic">{change.original}</p>
                                  </div>
                                  <div>
                                    <p className="font-medium mb-1">Filled:</p>
                                    <p className="text-foreground">{change.modified}</p>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Side-by-side comparison - only show for non-Excel files */}
      {!isExcelFile && (
      <div className="grid grid-cols-2 gap-4">
        {/* Original document */}
        <Card className="h-[700px] flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Original Template
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea 
              className="h-full px-6 overflow-x-hidden"
              onScroll={handleScroll('original')}
            >
              <div ref={originalRef} className="py-4 max-w-full overflow-hidden">
                {renderContentWithHighlights(originalContent, true)}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Generated document */}
        <Card className="h-[700px] flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-green-600" />
              Generated Document
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea 
              className="h-full px-6"
              onScroll={handleScroll('generated')}
            >
              <div ref={generatedRef} className="py-4">
                {renderContentWithHighlights(generatedContent, false)}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
      )}

    </div>
  );
}
